<template>
  <div>
    <highcharts :options="chartOptions" ref="chart" />
  </div>
</template>

<script>
import reportsMixin from '@/mixins/reportsMixin.js'
import Highcharts from 'highcharts'
import HighchartsOptions from '@/locales/highcharts-pt-br'
import HCMore from 'highcharts/highcharts-more'

HCMore(Highcharts)
Highcharts.setOptions(HighchartsOptions)

export default {
  name: 'ColumnRange',

  mixins: [reportsMixin],

  props: {
    /**
     * Dados para o eixo X do gráfico; array com os nomes das colunas
     * ['Segunda', Terça', 'Quarta', 'Quinta', 'Sexta']
     */
    xAxis: {
      type: [Array, Object],
      default: () => []
    },

    yAxis: {
      type: Array,
      default: () => {
        return [
          {
            title: {
              text: 'Eixo Y'
            }
          }
        ]
      }
    },

    /**
     * Dados que preenchem o gráfico para quando houver mais de um eixo y ou colunas agrupadas
     * [{
     *  name: 'Nome do eixo',
     *  type: 'column' OU 'spline',
     *  data: array com os dados,
     *  color: string com a cor
     * }]
     */
    seriesData: {
      type: Array,
      default: () => {
        return [
          {
            name: 'Nome do eixo',
            type: 'column',
            data: []
          }
        ]
      }
    },

    /**
     * {
     *   valuePrefix: 'R$ ',
     * }
     */
    tooltipFormat: {
      type: Object,
      default: () => {
        return {
          valueDecimals: 1,

          pointFormat:
            '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.low}</b> to <b>{point.high}</b><br>',

          formatter: function() {
            const monthIndex = this.point.index
            const monthName = this.series.chart.xAxis[0].categories[monthIndex]
            return `<b>${monthName}</b>`
          },

          positioner: function(labelWidth, labelHeight, point) {
            const chartWidth = this.chart.plotWidth
            let tooltipX = Math.max(
              0,
              Math.min(point.plotX - labelWidth + 250, chartWidth - labelWidth)
            )
            const tooltipY = point.plotY - labelHeight - 10

            if (chartWidth - tooltipX < 100) {
              tooltipX = point.plotX + 150
            } else if (chartWidth - tooltipX < 200 && labelWidth > 100) {
              tooltipX = point.plotX + 90
            }

            return { x: tooltipX, y: tooltipY }
          }
        }
      }
    },

    /**
     * {
     * text: 'Título do gráfico',
     * align: 'center'
     * }
     */
    title: {
      type: Object,
      default: () => {}
    },

    /** Opções de configuração do gráfico */
    plotOptions: {
      type: Object,
      default: () => {
        return {
          columnrange: {
            borderRadius: '50%',
            colorByPoint: true,
            pointWidth: 15,
            grouping: false,
            pointRange: 0.4,
            cursor: 'pointer',
            turboThreshold: 10000,
            series: {
              cursor: 'pointer'
            },

            states: {
              hover: {
                borderColor: '#e8e8e8',
                brightness: 0.3
              },
              inactive: {
                enabled: false
              }
            }
          }
        }
      }
    },

    /**
     * Subtítulo do gráfico, quando houver
     * {
     *  text:'Source: <a target="_blank"'>,
     *  align: 'left'
       }
     */
    subtitle: {
      type: Object,
      default: () => {}
    },

    /** Nome do arquivo de exportação */
    exportedFileName: {
      type: String,
      default: 'chart'
    },

    hasLegend: {
      type: Boolean,
      default: true
    },

    customHeight: {
      type: [String, Number],
      default: '200%'
    },

    /** Configurações para as opções de exportação do gráfico */
    exporting: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  data() {
    return {
      /** Dados de configuração do gráfico */
      chartOptions: {
        chart: {
          zoomType: 'none',
          type: 'columnrange',
          inverted: true,
          height: this.customHeight,
          loading: {
            labelStyle: {
              color: 'black'
            }
          }
        },

        /** Título */
        title: this.title,

        /** Subtitulo */
        subtitle: this.subtitle ? this.subtitle : null,

        /** Eixo X */
        xAxis: this.xAxis,

        /** Eixo Y */
        yAxis: this.yAxis,

        tooltip: this.tooltipFormat,

        plotOptions: this.plotOptions,

        legend: {
          enabled: this.hasLegend
        },

        /** Dados */
        series: this.seriesData,

        exporting: this.exporting
      }
    }
  },
  watch: {
    customHeight: {
      handler(newVal) {
        this.chartOptions.chart.height = newVal
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  text.highcharts {
    &-credits {
      display: none;
    }
  }
}
</style>
